import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentUser } from '../../context/CurrentUserContext';
import { refusalRedirectFor } from '../../utils/routeAccess';
import { useRefusalToast } from './useRefusalToast';
import { Role } from '../../generated/graphql';

interface RoleRouteProps {
  children: React.ReactNode;
  roles: readonly Role[];
}

/**
 * RoleRoute component protects routes that require one or more specific roles.
 * Accepts a `roles` prop listing all allowed roles.
 * Redirects unauthorized users to refusalRedirectFor(roles) (/dashboard; a sales-only user to /crm/partner-enquiries) with an error message.
 */
const RoleRoute: React.FC<RoleRouteProps> = ({ children, roles: allowedRoles }) => {
  const { user, loading: authLoading } = useAuth();
  const { roles: userRoles, loading: rolesLoading } = useCurrentUser();
  const location = useLocation();
  const isAuthorized = userRoles.some(role => allowedRoles.includes(role));

  // The permission toast fires from an effect, once per refusal (see useRefusalToast)
  useRefusalToast(
    'You do not have permission to access this page.',
    !authLoading && !rolesLoading && !!user && !isAuthorized
  );

  // Show loading spinner while checking authentication and roles
  if (authLoading || rolesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ returnUrl: location.pathname }} replace />;
  }

  // If authenticated but doesn't have a required role, redirect (dashboard; a sales-only user to Partner Enquiries) with error message
  if (!isAuthorized) {
    return <Navigate to={refusalRedirectFor(userRoles)} replace />;
  }

  // User is authenticated and has a required role, render children
  return <>{children}</>;
};

export default RoleRoute;
